// panel-autofocus-target.ts — release 29817 review (PSTUDIO-4784 follow-up)

/** The Analytics Events column id (`NodeColumns.analyticsEvent`). */
const ANALYTICS_EVENT_COLUMN = 'analyticsEvent';

export interface KeyboardOpenFocusState {
  /** The panel's open state AFTER the grid's Shift+Enter toggle. */
  isPanelOpenAfterToggle: boolean;
  /** The selected cell's column, if any. */
  columnId: string | null | undefined;
  /**
   * Whether the legacy Expanded Cell View is the panel being mounted
   * (`tableStore.shouldRenderExpandedCellView`). The node property panel that
   * replaces it behind a flag has no event select and does not read the nonce.
   */
  panelRendered: boolean;
}

/**
 * Whether the grid's Shift+Enter, having just OPENED the panel, should ask the
 * panel to move focus into its Analytics Events select.
 *
 * The grid cell of that column is itself a select, so no edit-entry gesture
 * sets `editingColumnId` for it and the panel's auto-open focus never fires.
 * Without this, Shift+Enter opened the panel with focus left on the td, and the
 * panel's select could be reached by mouse only (release 29817 browser pass:
 * 250 Tab and 40 Shift+Tab presses never reached it).
 *
 * The grid ASKS only on that column. That does not keep focus on the cell for
 * the others: the nonce is never reset (`requestExpandedCellViewAutofocus` only
 * increments it), and the panel's focus effect runs on every mount with any
 * non-zero value, so once a request has been made, any later opening of the
 * panel moves focus into its editor on every column (KEYBOARD_CLIPBOARD_BEHAVIOR
 * .md, "Limitation — any opening of the panel can move focus into it"). The
 * close direction is `shouldRestoreGridFocusAfterPanelToggle`.
 */
export function shouldFocusPanelSelectOnKeyboardOpen({
  isPanelOpenAfterToggle,
  columnId,
  panelRendered,
}: KeyboardOpenFocusState): boolean {
  return (
    isPanelOpenAfterToggle &&
    panelRendered &&
    columnId === ANALYTICS_EVENT_COLUMN
  );
}

/**
 * Which panel element an autofocus request lands on.
 *
 * On an Analytics Events cell it is the event select, unless that select is
 * disabled — then the JSON editor below it, which is what the request landed on
 * before the select existed. Every other column keeps the JSON editor, or the
 * textarea a scalar `richAssetContent` renders instead. Null when the panel has
 * none of them mounted (yet): the caller polls frames.
 */
export function resolvePanelAutofocusTarget<T>({
  columnId,
  analyticsSelect,
  jsonEditor,
  textarea,
}: {
  columnId: string | null | undefined;
  analyticsSelect: (T & { disabled?: boolean }) | null;
  jsonEditor: T | null;
  textarea: T | null;
}): T | null {
  if (
    columnId === ANALYTICS_EVENT_COLUMN &&
    analyticsSelect &&
    !analyticsSelect.disabled
  ) {
    return analyticsSelect;
  }
  return jsonEditor ?? textarea ?? null;
}
